import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { authClient } from '../../lib/auth-client';
import { beginIdentityTransition } from '../../lib/identity';
import { sessionQueryOptions } from '../../lib/session-query';
import { accountFromSession } from '../../lib/cache-account';
import { accountRef } from '../../lib/account';
import { OFFLINE_REASON, useSignOutGuard } from '../shell/SignOutButton';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { PageSkeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { Field, field } from '../create/Field';
import { IconTeam, IconWrenchBig } from '../icons';

/**
 * Where an invited person lands.
 *
 * This screen sits OUTSIDE the normal auth gate, because everything the gate would do to
 * this visitor is wrong: they usually have no account, and after making one they belong to
 * no shop — which would drop them on "create your shop", the single worst outcome here. A
 * new hire creating a second empty company is a mess only the owner can untangle.
 *
 * The address on the invite is not editable and not a field. It is the thing the server
 * checks, so offering to type a different one would just manufacture a failure.
 */

const ROLE_LABEL: Record<Role, string> = {
  owner: 'an owner',
  office: 'office staff',
  tech: 'a technician',
  bookkeeper: 'the bookkeeper',
};

export function JoinPage() {
  const { token } = useParams({ from: '/join/$token' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const invite = useQuery(trpc.team.lookupInvite.queryOptions({ token }));
  const session = useQuery(sessionQueryOptions(trpc));

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const accept = useMutation(trpc.team.acceptInvite.mutationOptions());
  // The one sign-out, guarded like the shells' (MAP 225b): it keeps and counts this
  // account's unsent work, and lands back on this invite so the right person can still join.
  const signOutGuard = useSignOutGuard(
    session.data ? accountRef(accountFromSession(session.data)) : null,
    `/join/${token}`,
  );

  // A transport failure must never be dressed up as "bad invite" — the difference between
  // "your link is wrong" and "we can't reach the server" is the difference between a hire
  // giving up and a hire retrying in a minute.
  if (invite.isPending || session.isPending)
    return (
      <Centered>
        <PageSkeleton />
      </Centered>
    );
  if (invite.isError || session.isError) {
    return (
      <Centered>
        <EmptyState
          icon={IconWrenchBig}
          title="Couldn't reach Servwell"
          description="We couldn't check this invite. Check your connection and try again."
          action={
            <Button
              onClick={() => {
                void invite.refetch();
                void session.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      </Centered>
    );
  }

  const data = invite.data;
  if (data.status !== 'ok') {
    const copy = {
      expired: {
        title: 'This invite has expired',
        description: 'Invites last a week. Ask the shop to send you a new one.',
      },
      used: {
        title: 'This invite has already been used',
        description:
          'If you already set up your login, sign in as normal. Otherwise ask the shop for a new invite.',
      },
      unknown: {
        title: "We don't recognise this invite",
        description:
          'The link may be incomplete — they get cut off in text messages. Ask for it again.',
      },
    }[data.status];
    return (
      <Centered>
        <EmptyState
          icon={IconTeam}
          title={copy.title}
          description={copy.description}
          action={
            <Button onClick={() => void navigate({ to: '/' })}>Go to sign in</Button>
          }
        />
      </Centered>
    );
  }

  // Bound to consts so the narrowing above survives into the closure below.
  const invited = data;
  const signedInAs = session.data.authenticated ? (session.data.email ?? null) : null;
  const wrongAccount =
    signedInAs !== null && signedInAs.toLowerCase() !== invited.email.toLowerCase();

  /** Join, then land on the role's home. One button, whatever state they arrived in. */
  async function join(mode: 'signup' | 'signin' | 'already') {
    setAuthError(null);
    setWorking(true);
    try {
      // Signing up, signing in and joining all move this browser's cookie to another account
      // or shop. Other tabs stop drawing the old one before any of it lands (MAP 225a).
      beginIdentityTransition();
      if (mode === 'signup') {
        const r = await authClient.signUp.email({
          email: invited.email,
          password,
          name: name.trim() || invited.email,
        });
        if (r.error) throw new Error(r.error.message ?? 'Could not create your login.');
      } else if (mode === 'signin') {
        const r = await authClient.signIn.email({ email: invited.email, password });
        if (r.error) throw new Error(r.error.message ?? 'That password did not work.');
      }
      await accept.mutateAsync({ token });
      // The session's org changed server-side; every cached answer was taken under the old
      // one, so none of it can be trusted for the shop they just walked into.
      await qc.invalidateQueries();
      await navigate({ to: '/' });
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <Centered>
      <div className="space-y-4 rounded-[var(--radius)] border border-border bg-surface p-6">
        <div>
          <p className="font-heading text-2xl font-bold">{data.shopName}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You've been invited to join as {ROLE_LABEL[data.role]}.
          </p>
          <p className="mt-2 text-sm">{data.email}</p>
        </div>

        {authError ? <Notice tone="danger">{authError}</Notice> : null}

        {wrongAccount ? (
          <>
            <Notice tone="warn">
              You're signed in as {signedInAs}, but this invite is for {data.email}.
            </Notice>
            <Button
              className={`w-full ${signOutGuard.offline ? 'opacity-60' : ''}`}
              variant="ghost"
              disabled={working}
              onClick={signOutGuard.request}
              aria-busy={signOutGuard.asking || undefined}
              aria-describedby={signOutGuard.offline ? signOutGuard.reasonId : undefined}
            >
              Sign out and use {data.email}
            </Button>
            {signOutGuard.offline ? (
              <p
                id={signOutGuard.reasonId}
                className="text-center text-xs text-muted-foreground"
              >
                {OFFLINE_REASON}
              </p>
            ) : null}
            {signOutGuard.dialog}
          </>
        ) : signedInAs ? (
          <Button
            className="w-full"
            disabled={working}
            onClick={() => void join('already')}
          >
            {working ? 'Joining…' : `Join ${data.shopName}`}
          </Button>
        ) : data.hasAccount ? (
          <>
            <p className="text-sm text-muted-foreground">
              You already have a Servwell login. Enter your password to join this shop.
            </p>
            <Field label="Password">
              <input
                className={field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </Field>
            <Button
              className="w-full"
              disabled={working || password.length === 0}
              onClick={() => void join('signin')}
            >
              {working ? 'Joining…' : `Sign in and join`}
            </Button>
          </>
        ) : (
          <>
            <Field label="Your name">
              <input
                className={field}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                placeholder="Dana Ruiz"
              />
            </Field>
            <Field label="Pick a password">
              <input
                className={field}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">At least 8 characters.</p>
            </Field>
            <Button
              className="w-full"
              disabled={working || password.length < 8 || !name.trim()}
              onClick={() => void join('signup')}
            >
              {working ? 'Setting you up…' : `Join ${data.shopName}`}
            </Button>
          </>
        )}
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
